import type { Account, ApiKitTrip } from '@tuic/api-kit';
import { toRezonedDateTime } from '@lib/date';
import { DateTime } from 'luxon';
import { PhasedBooking } from '@lib/phased-booking';
import { getNowBasedOnBooking } from '@lib/booking';

/**
 * Sorts the given trips collection by startDateTime in ascending order.
 */
export const sortTrips = (trips: ApiKitTrip[]): ApiKitTrip[] => {
  return [...trips].sort((a, b) => {
    const aTimestamp = a.startDateTime
      ? new Date(a.startDateTime).getTime()
      : Infinity;
    const bTimestamp = b.startDateTime
      ? new Date(b.startDateTime).getTime()
      : Infinity;

    return aTimestamp - bTimestamp;
  });
};

export const sortBookingTrips = (
  trips: Account.MyTrip2[],
): Account.MyTrip2[] => {
  return [...trips].sort((a, b) => {
    const aTimestamp = a.startDate ? new Date(a.startDate).getTime() : Infinity;
    const bTimestamp = b.startDate ? new Date(b.startDate).getTime() : Infinity;

    return aTimestamp - bTimestamp;
  });
};

/**
 * Returns the active trip from given trips collection or undefined if there is no active trip.
 */
export const getActiveTrip = (
  booking: PhasedBooking,
): ApiKitTrip | undefined => {
  const tripCode = selectActiveTripCodeFromBooking(booking);

  if (typeof tripCode === 'undefined') {
    return undefined;
  }

  return booking.trips.find(trip => trip.code === tripCode);
};

/*
 * Helper type for trips with minimal required fields for selection logic
 */
type KindOfAccountTrip = {
  tripCode: string;
  startDate?: string | null;
  endDate?: string | null;
  cabins: Array<{
    travellerIds: string[];
    [key: string]: any;
  }>;
  [key: string]: any;
};

/*
 * Selects active trip code based on date and traveller location
 * Prioritizes trips where the traveller is actually booked
 */
export const selectActiveTripCodeFromTrips = (
  trips: Account.MyTrip2[] | KindOfAccountTrip[],
  travellerId: string | undefined,
  now: DateTime,
): string | undefined => {
  // Find all active trips based on date
  const activeTrips = trips.filter(trip => {
    if (!trip.startDate || !trip.endDate) {
      return false;
    }

    const startDate = toRezonedDateTime(trip.startDate).startOf('day');
    const endDate = toRezonedDateTime(trip.endDate).endOf('day');

    const hasBegun = startDate < now;
    const hasEnded = endDate < now;

    return hasBegun && !hasEnded;
  });

  // No active trips
  if (activeTrips.length === 0) {
    return undefined;
  }

  // Only one active trip
  if (activeTrips.length === 1) {
    return activeTrips[0].tripCode;
  }

  // Multiple active trips - reverse to get last items first (maintaining findLast behavior)
  const reversedActiveTrips = [...activeTrips].reverse();

  // If we don't have traveller ID, return the last one (now first after reverse)
  if (typeof travellerId === 'undefined') {
    return reversedActiveTrips[0].tripCode;
  }

  // Find where traveller is booked (searching from last to first)
  const activeTripWithTraveller = reversedActiveTrips.find(trip =>
    trip.cabins?.some(cabin => cabin.travellerIds?.includes(travellerId)),
  );

  if (activeTripWithTraveller) {
    return activeTripWithTraveller.tripCode;
  }

  // Traveller not found in any active trip, return the last active trip
  return reversedActiveTrips[0].tripCode;
};

/*
 * Selects active trip from booking
 */
export function selectActiveTripCodeFromBooking(
  booking: Account.MyBooking2,
  now: DateTime,
): string | undefined;
export function selectActiveTripCodeFromBooking(
  booking: PhasedBooking,
): string | undefined;
export function selectActiveTripCodeFromBooking(
  booking: Account.MyBooking2 | PhasedBooking,
  now?: DateTime,
): string | undefined {
  if ('original' in booking) {
    const actualNow = getNowBasedOnBooking(booking);
    return selectActiveTripCodeFromTrips(
      booking.original.trips,
      booking.original.travellerId,
      actualNow,
    );
  }

  // MyBooking2WithTravelPlanInfo
  if (typeof now === 'undefined') {
    throw new Error(
      'now parameter is required for MyBooking2WithTravelPlanInfo',
    );
  }

  return selectActiveTripCodeFromTrips(booking.trips, booking.travellerId, now);
}

/**
 * Returns the active trip from given trips collection or the first one if there is no active trip.
 */
export const getActiveTripOrFirst = (booking: PhasedBooking): ApiKitTrip => {
  const active = getActiveTrip(booking);

  if (active) {
    return active;
  }

  return sortTrips(booking.trips)[0];
};

/**
 * Returns the active trip code from given booking or undefined if there is no active trip.
 */
export const getActiveTripCode = (
  booking: PhasedBooking | Account.MyBooking2,
): string | undefined => {
  if ('original' in booking) {
    // PhasedBooking
    return selectActiveTripCodeFromBooking(booking);
  }
  // MyBooking2WithTravelPlanInfo
  const now = DateTime.now();
  return selectActiveTripCodeFromBooking(booking, now);
};

/**
 * Returns the active trip code from given booking or the first one if there is no active trip.
 */
export const getActiveTripCodeOrFirst = (
  booking: PhasedBooking | Account.MyBooking2,
): string | undefined => {
  const tripCode = getActiveTripCode(booking);

  if (tripCode) {
    return tripCode;
  }

  if ('original' in booking) {
    // This is a PhasedBooking
    return sortTrips(booking.trips)[0]?.code;
  }

  // This is a MyBooking2WithTravelPlanInfo
  return sortBookingTrips(booking.trips)[0]?.tripCode;
};
